import getVideoThumbnail from 'get-video-thumbnail'
import { debounce } from 'lodash'
import { useCallback, useEffect, useMemo, useState } from 'react'
interface ThumbnailOptions {
  time?: number
  width?: number
  height?: number
}
const checkImageExists = async (url: string): Promise<boolean> => {
  return new Promise((resolve) => {
    const img = new Image()
    img.onload = () => resolve(true)
    img.onerror = () => resolve(false)
    img.src = url
  })
}

const getYoutubeThumbnail = async (videoId: string) => {
  const resolutions = [
    'maxresdefault.jpg',
    'hqdefault.jpg',
    'mqdefault.jpg',
    'sddefault.jpg',
  ]

  for (const resolution of resolutions) {
    const url = `https://img.youtube.com/vi/${videoId}/${resolution}`
    const exists = await checkImageExists(url)
    if (exists) {
      return url
    }
  }
  return `https://img.youtube.com/vi/${videoId}/default.jpg`
}

const getThumbnail = async (
  videoUrl: string,
  options?: ThumbnailOptions
): Promise<string | undefined> => {
  const youtubeRegex =
    /(?:youtube\.com\/(?:[^\/\n\s]+\/\S+\/|(?:v|e(?:mbed)?)\/|\S*?[?&]v=|shorts\/)|youtu\.be\/)([a-zA-Z0-9_-]{11})/
  const match = videoUrl.match(youtubeRegex)

  if (match && match[1]) {
    // Extract the video ID and return the thumbnail URL
    const videoId = match[1]
    const imgUrl = await getYoutubeThumbnail(videoId)
    return imgUrl
  }

  try {
    // For non-YouTube URLs, use getVideoThumbnail
    const thumbnail = await getVideoThumbnail(videoUrl, options)
    return thumbnail.thumbnailSrc
  } catch (error) {
    console.error('Error fetching thumbnail:', error)
    return undefined
  }
}
const useThumbnail = (videoUrl: string, options?: ThumbnailOptions) => {
  const [thumbnailSrc, setThumbnailSrc] = useState<string | undefined>(
    undefined
  )
  const [isMounted, setIsMounted] = useState(false)
  const debouncedGetThumbnail = useCallback(
    debounce(async (url: string, opts: ThumbnailOptions | undefined) => {
      if (isMounted && typeof window !== 'undefined') {
        const thumbnailSrc = await getThumbnail(url, opts)
        setThumbnailSrc(thumbnailSrc)
      }
    }, 500),
    [isMounted]
  )
  useEffect(() => {
    setIsMounted(true)
    return () => {
      setIsMounted(false)
    }
  }, [])
  useMemo(() => {
    if (typeof window !== 'undefined') {
      debouncedGetThumbnail(videoUrl, options)
    }
  }, [videoUrl, options, debouncedGetThumbnail])

  return thumbnailSrc
}

export default useThumbnail
